import { AbstractControl } from '@angular/forms';

export const functions = {
    getFilenameFromContentDisposition: (contentDisposition: string): string => {
        if (!contentDisposition) {
            return '';
        }

        // Prefer RFC 5987 / 6266: filename*=UTF-8''<url-encoded>
        const filenameStarRegex =
            /filename\*\s*=\s*(?<charset>[^']*)''(?<value>[^;]+)/i;
        const filenameStarMatch = filenameStarRegex.exec(contentDisposition);
        if (filenameStarMatch?.groups?.value) {
            const charset = (filenameStarMatch.groups.charset || '').trim();
            const encoded = filenameStarMatch.groups.value.trim();

            // Most backends use UTF-8; decodeURIComponent handles %XX.
            // If decoding fails, fall back to the raw value.
            try {
                const decoded = decodeURIComponent(encoded);
                return charset.toLowerCase() === 'utf-8' || charset === ''
                    ? decoded
                    : decoded;
            } catch {
                return encoded;
            }
        }

        // Fallback: filename="..." or filename=...
        const filenameRegex = /filename\s*=\s*(?<filename>[^;]+)/i;
        const filenameMatch = filenameRegex.exec(contentDisposition);
        const fileName = filenameMatch?.groups?.filename?.trim() || '';
        return fileName.replace(/^"(.*)"$/, '$1');
    },

    convertToValidFilename(fileName): string {
        return fileName.replace(/[\/|\\:*?"<>]/g, ' ');
    },

    downloadBlobIntoFile: (blob: Blob, fileName: string = ''): void => {
        const anchorElement = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        anchorElement.href = url;
        anchorElement.style.display = 'none';
        anchorElement.download = functions.convertToValidFilename(fileName);
        anchorElement.target = '_blank';
        document.body.appendChild(anchorElement);
        anchorElement.click();
        document.body.removeChild(anchorElement);
        window.URL.revokeObjectURL(url);
    },

    roundNumber: (value: number, precision: number = 0): number => {
        if (!value || isNaN(value)) {
            return value;
        }
        const multiplier = Math.pow(10, precision);
        value *= multiplier;
        value = Math.round(value);
        value /= multiplier;
        return value;
    },

    /**
     * Formats a number with thousands separators using US locale
     * @param value The number to format
     * @returns Formatted string with commas as thousands separators (e.g., "1,234.56")
     */
    formatNumber: (value: number): string => {
        return value?.toLocaleString('en-US') || '';
    },

    /**
     * Parses a formatted number string back to a number
     * @param value Formatted string with commas (e.g., "1,234.56")
     * @returns Parsed number value
     */
    parseNumberFromString: (value: string): number => {
        return parseFloat(value.replace(/,/g, '')) || 0;
    },

    groupBy: <T, K>(
        list: T[],
        keyGetter: (item: T) => K,
        keyCompare: (a: K, b: K) => boolean = (a, b) => a === b
    ): { key: K; items: T[] }[] => {
        return list.reduce<{ key: K; items: T[] }[]>((acc, x) => {
            const key = keyGetter(x);
            let group = acc.find(a => keyCompare(a.key, key));

            if (!group) {
                group = { key, items: [] };
                acc.push(group);
            }

            group.items.push(x);

            return acc;
        }, []);
    },

    // cspell:disable
    /**
     * @description To guarantee type safety of "string-ly typed" property names like name-of in C#
     * If the names of the innovation properties ever change, TypeScript will error on compile.
     * @link https://schneidenbach.gitbooks.io/typescript-cookbook/content/nameof-operator.html
     * @author @omar-elsayed97
     * @param name the name of the property
     * @returns
     */
    // cspell:enable
    nameOf: <T>(name: keyof T): keyof T => name,

    /**
     * @description This method is a utility function that recursively checks if an `AbstractControl` object or its
     * nested controls have the `required` validator, and returns `true` if any are found, and `false` otherwise.
     * This method does the same as the deprecated `hasValidator` method used in previous versions of Angular.
     * @author @omar-elsayed97
     * @param abstractControl the control field
     * @returns boolean
     */
    hasRequiredField: (abstractControl: AbstractControl): boolean => {
        if (abstractControl.validator) {
            const validator = abstractControl.validator({} as AbstractControl);
            if (validator && validator.required) {
                return true;
            }
        }
        if (abstractControl['controls']) {
            for (const controlName in abstractControl['controls']) {
                if (abstractControl['controls'][controlName]) {
                    if (
                        functions.hasRequiredField(
                            abstractControl['controls'][controlName]
                        )
                    ) {
                        return true;
                    }
                }
            }
        }
        return false;
    },

    /**
     * @description Formats the value of a date field as a bare `yyyy-MM-dd` calendar day, for the
     * fields that mean a day rather than an instant. The default `toISOString` serialization reads
     * the local midnight a picker emits back out in UTC, which lands on an adjacent day at any
     * non-zero offset, so such a field has to travel without a time component.
     *
     * A string arrives from the api already carrying the day that is stored, and is therefore
     * truncated as-is; only a `Date` the user picked is read through the local calendar, which is
     * the one the picker and the display both show. Parsing the api value into a `Date` instead
     * would push an untouched field onto an adjacent day for any browser whose offset disagrees
     * with the stored value.
     * @param value the picked date, a date string from the api, or nothing
     * @returns the calendar day as `yyyy-MM-dd`, or null when there is no usable value
     */
    toDateOnlyString: (
        value: Date | string | null | undefined
    ): string | null => {
        if (!value) {
            return null;
        }

        if (typeof value === 'string') {
            const storedDay = /^(\d{4}-\d{2}-\d{2})/.exec(value);
            return storedDay ? storedDay[1] : null;
        }

        if (isNaN(value.getTime())) {
            return null;
        }

        const month = `${value.getMonth() + 1}`.padStart(2, '0');
        const day = `${value.getDate()}`.padStart(2, '0');

        return `${value.getFullYear()}-${month}-${day}`;
    },
};
